import {
  annualFreeMonths,
  annualMonthly,
  type BillingInterval,
  type ChannelSales,
  type CreditPackId,
  entitlementStatusSchema,
  formatPlanPrice,
  PLAN_CATALOG,
  planAtLeast,
  type EntitlementStatus,
  type PaidPlan,
  type PlanTier,
} from '@secretario/shared';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from './api/http.js';
import { siteOrigin } from './invite.js';
import { isNativeShell } from './platform.js';
import {
  buyCreditsInStore,
  buyInStore,
  restoreStorePurchases,
  storeChannel,
  storeManagementUrl,
  storePrices,
  storeReady,
  type StorePrices,
} from './storeBilling.js';

export const ENTITLEMENTS_KEY = ['entitlements'] as const;

export async function getEntitlements(): Promise<EntitlementStatus> {
  return entitlementStatusSchema.parse(await apiFetch<unknown>('/v1/entitlements/me'));
}

/** Plano em vigor, uso do dia e o que pode ser assinado agora. */
export function useEntitlements(enabled = true) {
  return useQuery({
    queryKey: ENTITLEMENTS_KEY,
    queryFn: getEntitlements,
    staleTime: 30_000,
    enabled,
  });
}

export const STORE_PRICES_KEY = ['store-prices'] as const;

/**
 * Preços dos planos vindos da loja do aparelho. Só roda no app instalado com
 * a loja ligada; na web o preço é o do catálogo. `failed` = a loja não
 * respondeu (sem preço, sem compra até ela voltar).
 */
export function useStorePrices(): {
  prices: StorePrices | null;
  failed: boolean;
  loading: boolean;
} {
  const query = useQuery({
    queryKey: STORE_PRICES_KEY,
    queryFn: storePrices,
    enabled: storeReady(),
    staleTime: 10 * 60_000,
    retry: 1,
  });
  if (!storeReady()) return { prices: null, failed: false, loading: false };
  return { prices: query.data ?? null, failed: query.isError, loading: query.isPending };
}

/**
 * "R$ 39,90 por mês" ou "R$ 399,00 por ano". Na web, o preço vem do catálogo;
 * no app instalado, é a loja quem diz o valor: sem a resposta dela, null (a tela explica).
 */
export function priceLine(
  plan: PlanTier,
  store?: StorePrices | null,
  interval: BillingInterval = 'month',
): string | null {
  const entry = PLAN_CATALOG[plan];
  if (entry.price === 0) return 'Sem custo';
  const unit = interval === 'year' ? 'por ano' : 'por mês';
  if (isNativeShell()) {
    const price = (interval === 'year' ? store?.annual : store?.monthly)?.[plan as PaidPlan];
    return price ? `${price} ${unit}` : null;
  }
  const cents = interval === 'year' ? entry.annualPrice : entry.price;
  return cents === null ? null : `${formatPlanPrice(cents)} ${unit}`;
}

/** O preço do catálogo ("R$ 39,90 por mês"), para mostrar quando a loja não trouxe o dela. */
export function catalogPriceLine(
  plan: PlanTier,
  interval: BillingInterval = 'month',
): string | null {
  const entry = PLAN_CATALOG[plan];
  if (entry.price === 0) return 'Sem custo';
  const cents = interval === 'year' ? entry.annualPrice : entry.price;
  return cents === null
    ? null
    : `${formatPlanPrice(cents)} ${interval === 'year' ? 'por ano' : 'por mês'}`;
}

/** A loja respondeu, mas sem este plano à venda (produto ainda não criado nela). */
export const STORE_MISSING =
  'Assinar pelo app ainda não está disponível nesta versão. Os valores dos planos são estes.';

/** "Equivale a R$ 33,25 por mês, 2 meses de presente" (só no anual). */
export function annualHint(plan: PlanTier, store?: StorePrices | null): string | null {
  if (plan === 'free') return null;
  const perMonth = isNativeShell()
    ? store?.annualPerMonth[plan as PaidPlan]
    : (() => {
        const cents = annualMonthly(plan);
        return cents === null ? null : formatPlanPrice(cents);
      })();
  if (!perMonth) return null;
  const months = annualFreeMonths(plan);
  const gift = months === 1 ? '1 mês de presente' : months > 1 ? `${months} meses de presente` : '';
  return `Equivale a ${perMonth} por mês${gift ? `, ${gift}` : ''}`;
}

/**
 * O teste grátis aparece neste plano para esta pessoa? Na web, o servidor diz;
 * no app, a loja também precisa oferecer o teste no produto.
 */
export function trialHere(
  plan: PlanTier,
  status: EntitlementStatus | undefined,
  store?: StorePrices | null,
): number {
  const days = PLAN_CATALOG[plan].trialDays;
  if (!days || !status?.trialEligible) return 0;
  if (isNativeShell()) return store?.trial[plan as PaidPlan] ? days : 0;
  return days;
}

/** O anual está à venda neste canal agora (e, no app, a loja tem o preço dele)? */
export function annualHere(status: EntitlementStatus | undefined, store?: StorePrices | null) {
  if (!salesHere(status).annual) return false;
  if (!isNativeShell()) return true;
  return Boolean(store?.annual.basic || store?.annual.pro);
}

/** O que dizer quando a loja não devolveu os preços. */
export const STORE_SILENT = 'A loja não respondeu agora. Tenta de novo daqui a pouco.';

/**
 * Onde a compra acontece. No navegador, pela página de pagamento; no app
 * instalado, pela loja do aparelho quando ela está ligada. Sem a loja, o app
 * instalado não tem botão nem link para pagamento de fora.
 */
export function canBuyHere(): boolean {
  return !isNativeShell() || storeReady();
}

/** Canal de venda deste lugar: a loja do aparelho no app instalado, o site no navegador. */
export function channelHere(): 'web' | 'google_play' | 'app_store' {
  return (isNativeShell() && storeChannel()) || 'web';
}

const NO_SALES: ChannelSales = { basic: false, pro: false };

/** Quais planos este canal (site ou loja deste aparelho) vende agora. */
export function salesHere(status: EntitlementStatus | undefined): ChannelSales {
  if (!status) return NO_SALES;
  return status.salesBy[channelHere()] ?? NO_SALES;
}

/**
 * A assinatura em vigor foi feita por outro canal (site num app, loja no site)?
 * Nesse caso a troca e o cancelamento são por lá; aqui não há botão de compra.
 */
export function otherChannel(
  subscription: EntitlementStatus['subscription'],
): 'web' | 'google_play' | 'app_store' | null {
  if (!subscription || subscription.source === 'grant') return null;
  return subscription.source === channelHere() ? null : subscription.source;
}

/** "Sua assinatura é pela Google Play: mude por lá." */
export function otherChannelLine(channel: 'web' | 'google_play' | 'app_store'): string {
  const where =
    channel === 'web'
      ? 'pelo site'
      : channel === 'app_store'
        ? 'pela App Store'
        : 'pela Google Play';
  return `Sua assinatura é ${where}: mude por lá.`;
}

/** Endereço para gerenciar a assinatura de outro canal (abre fora deste app). */
export function otherChannelUrl(channel: 'web' | 'google_play' | 'app_store'): string {
  if (channel === 'web') return `${siteOrigin()}/plano`;
  if (channel === 'app_store') return 'https://apps.apple.com/account/subscriptions';
  return 'https://play.google.com/store/account/subscriptions';
}

/** Avisa o servidor para conferir a loja agora (depois de comprar ou restaurar). */
export async function syncStorePurchases(): Promise<void> {
  await apiFetch<void>('/v1/billing/store/sync', { method: 'POST' }).catch(() => undefined);
}

/** Espera o servidor receber o aviso da loja e liberar o plano (alguns segundos). */
async function waitForPlan(plan: PaidPlan): Promise<boolean> {
  for (let attempt = 0; attempt < 12; attempt += 1) {
    const status = await getEntitlements().catch(() => null);
    if (status && planAtLeast(status.plan, plan)) return true;
    await new Promise((resolve) => setTimeout(resolve, 2_500));
  }
  return false;
}

/**
 * Abre o pagamento do plano. Devolve true quando o plano mudou na hora (troca
 * de plano). No app instalado, `status` diz se já existe assinatura da loja:
 * aí a compra vira troca (subir cobra a diferença; descer vale na renovação).
 */
export async function startCheckout(
  plan: PaidPlan,
  status?: EntitlementStatus,
  interval: BillingInterval = 'month',
): Promise<boolean> {
  if (isNativeShell()) {
    const subscription = status?.subscription;
    const current =
      subscription && subscription.source === channelHere()
        ? { plan: subscription.plan, productId: subscription.productId ?? null }
        : null;
    if (!(await buyInStore(plan, current, interval))) return false;
    await syncStorePurchases();
    return waitForPlan(plan);
  }
  const result = await apiFetch<{ url: string | null; changed: boolean }>('/v1/billing/checkout', {
    method: 'POST',
    body: { plan, interval },
  });
  if (result.url) {
    window.location.assign(result.url);
    return false;
  }
  return result.changed;
}

/** "Restaurar compras" no app instalado: a loja devolve a assinatura e o servidor confere. */
export async function restorePurchases(): Promise<void> {
  await restoreStorePurchases();
  await syncStorePurchases();
}

/** Créditos à venda por este canal (loja no app instalado, pagamento na web). */
export function canBuyCreditsHere(webSales: boolean): boolean {
  return isNativeShell() ? storeReady() : webSales;
}

/** Espera o saldo de créditos subir depois da compra na loja (o aviso chega em segundos). */
async function waitForCredits(before: number): Promise<boolean> {
  for (let attempt = 0; attempt < 12; attempt += 1) {
    const status = await getEntitlements().catch(() => null);
    if (status && status.features.credits.balance > before) return true;
    await new Promise((resolve) => setTimeout(resolve, 2_500));
  }
  return false;
}

/**
 * Compra de um pacote de créditos. No app instalado, pela loja (compra
 * consumível); na web, pela página de pagamento. true quando o saldo já subiu.
 */
export async function startCreditPurchase(pack: CreditPackId, balance: number): Promise<boolean> {
  if (isNativeShell()) {
    if (!(await buyCreditsInStore(pack))) return false;
    await syncStorePurchases();
    return waitForCredits(balance);
  }
  const result = await apiFetch<{ url: string }>('/v1/billing/credits/checkout', {
    method: 'POST',
    body: { pack },
  });
  window.location.assign(result.url);
  return false;
}

/** Portal do cliente: cartão, recibos e cancelamento. */
export async function openBillingPortal(): Promise<void> {
  if (isNativeShell()) {
    window.open(await storeManagementUrl(), '_system');
    return;
  }
  const result = await apiFetch<{ url: string }>('/v1/billing/portal', { method: 'POST' });
  window.location.assign(result.url);
}

// ── "No plano Completo": qualquer tela abre a folha, dizendo o motivo ──────
export const PAYWALL_EVENT = 'secretario:planos';

/** O momento em que a pessoa esbarrou num limite ou num recurso de plano maior. */
export type PaywallReason =
  | 'limit'
  | 'missed'
  | 'meeting'
  | 'services'
  | 'research'
  | 'news'
  | 'clients'
  | 'quick-replies'
  | 'schedule'
  | 'agenda'
  | 'avatar'
  | 'voice';

export function openPaywall(reason: PaywallReason, surface: string): void {
  window.dispatchEvent(new CustomEvent(PAYWALL_EVENT, { detail: { reason, surface } }));
}
